import type { ValidationError } from './utils';
import type { FieldErrors, FieldValues, UseFormSetError, Path } from 'react-hook-form';

/**
 * Scrolls to the first field with an error and focuses it.
 * Uses the field name to find the input by id or name attribute.
 */
export function scrollToFirstError<T extends FieldValues>(errors: FieldErrors<T>): void {
  const firstErrorKey = Object.keys(errors)[0];
  if (!firstErrorKey) return;

  // Small delay to ensure DOM has updated with error states
  setTimeout(() => {
    // Try multiple selector strategies
    const selectors = [
      `[id="${firstErrorKey}"]`,
      `[name="${firstErrorKey}"]`,
      `[id*="${firstErrorKey}"]`,
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (element as HTMLElement).focus();
        return;
      }
    }
  }, 50);
}

/**
 * Merges API validation errors into react-hook-form.
 * Maps backend field names to form field errors.
 */
export function mergeApiErrors<T extends FieldValues>(
  apiErrors: ValidationError[],
  setError: UseFormSetError<T>,
): void {
  apiErrors.forEach((err) => {
    setError(err.field as Path<T>, {
      type: 'server',
      message: err.message,
    });
  });

  // Scroll to first error after setting API errors
  if (apiErrors.length > 0) {
    setTimeout(() => {
      const firstField = apiErrors[0].field;
      const element = document.querySelector(`[id="${firstField}"], [name="${firstField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (element as HTMLElement).focus();
      }
    }, 50);
  }
}
